import React, { useState } from "react"
import { AppBar, Toolbar, Container, Box, useTheme, useMediaQuery } from "@material-ui/core"
import { NavLink, useLocation } from "react-router-dom"
import Config from "react-global-configuration"
import { makeStyles } from "@material-ui/styles"
import { NavLinkItem, Logo } from ".."
import HeaderTray from "./HeaderTray"
import { useAuth } from "../../services"
import { useQuerySettings } from "../../data"
import { PunchClock } from "../PunchClock"

const useStyles = makeStyles((theme) => ({
  header: {
    backgroundColor: "#ffffff",
    color: "#000000",
    height: ({ hasPunchClock }) =>
      hasPunchClock
        ? theme.dimensions.header.height + theme.dimensions.punchClock.height
        : theme.dimensions.header.height,
    paddingRight: 0,
    boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.16)",
    overflow: ({ hasPunchClock }) => (hasPunchClock ? "hidden" : "visible"),
    transition: "height 0.6s ease",
  },
  toolbar: {
    height: theme.dimensions.header.height,
    paddingRight: 0,
  },
  desktopLogo: {
    width: 34,
    height: 34,
  },
  mobileLogo: {
    display: "flex",
    flexDirection: "column",
  },
  nav: {
    display: "flex",
    flexDirection: "row",
    marginLeft: 16,
  },
  navLink: {
    color: "#000000 !important",
    // lineHeight: "1.08",
    fontFamily: theme.typography.fontFamily,
    marginRight: 0,
    padding: ({ short }) => (short ? "0 10px" : "0 15px"),
    textAlign: "center",
    fontSize: 13,
    fontWeight: "600",
    height: 60,
  },
  navLinkWithMenu: {
    padding: ({ short }) => (short ? "0 10px 0 5px" : "0 15px 0 7px"),
  },
  navLinkActive: {
    background: "linear-gradient(to right, #f63b3b, #fd9317) left 55px no-repeat",
    backgroundSize: "100% 3px",
  },
}))

const Header = () => {
  const theme = useTheme()
  const short = useMediaQuery(theme.breakpoints.down("sm"))
  const location = useLocation()
  const [hasPunchClock, setHasPunchClock] = useState(false)
  const {
    authed,
    permissionGroups: { readJobs, readKnowledge, readTemplates, readReports, adminTraining },
    location: userLocation,
    hasFeature,
    hasPermission,
  } = useAuth()
  const { lang } = useQuerySettings()
  const { clientKiosk: kiosk } = Config.get()
  const classes = useStyles({ short, hasPunchClock })

  if (!authed) return null

  const getNavLinkClassName = (areas) => {
    const pathStartsWith = (area) => location.pathname.startsWith(area)
    const active = Array.isArray(areas) ? areas.some((area) => pathStartsWith(area)) : pathStartsWith(areas)

    return `${classes.navLink} ${active ? classes.navLinkActive : ""}`
  }

  const langArea = short ? lang.areaShort : lang.area

  const hasSensors = hasFeature("sensors") && hasPermission("sensor_read")
  const hasTraining = hasFeature("training") && hasPermission("training_create")
  const hasTimesheets = hasFeature("timesheets")
  const hasManagersHub =
    hasPermission([...readTemplates, "user_read_admin", ...readReports]) || hasSensors || hasTraining

  const handleKioskLoggingOut = () => {
    setHasPunchClock(false)
  }

  const handlePunchClockLoaded = (show) => {
    setHasPunchClock(show)
  }

  return (
    <>
      <AppBar position="sticky" className={classes.header}>
        <Container>
          <Toolbar className={classes.toolbar} disableGutters>
            {authed && (
              <>
                <Box display={{ xs: "none", sm: "block" }}>
                  <NavLink to="/dashboard">
                    <Logo className={classes.desktopLogo} link={false} />
                  </NavLink>
                </Box>

                <Box display={{ xs: "block", sm: "none" }}>
                  <NavLink to="/dashboard" className={classes.mobileLogo}>
                    <Logo size={26} link={false} />
                  </NavLink>
                </Box>

                <Box display={{ xs: "none", sm: "block" }}>
                  <nav className={classes.nav}>
                    <NavLinkItem
                      to="/dashboard"
                      text={langArea.dashboard}
                      className={getNavLinkClassName("/dashboard")}
                      disabled={!userLocation}
                    />
                    <NavLinkItem
                      requires={readJobs}
                      to="/jobs"
                      text={langArea.jobs}
                      className={getNavLinkClassName("/jobs")}
                      disabled={!userLocation}
                    />
                    <NavLinkItem
                      requires={readKnowledge}
                      operator="and"
                      to="/knowledge"
                      text={langArea.knowledge}
                      className={getNavLinkClassName("/knowledge")}
                    />
                    <NavLinkItem
                      requires="post_read"
                      to="/hub"
                      text={langArea.hub}
                      className={getNavLinkClassName("/hub")}
                      disabled={!userLocation}
                    />
                    {hasManagersHub && !kiosk && (
                      <NavLinkItem
                        text={langArea.manager}
                        className={`${getNavLinkClassName([
                          "/people",
                          "/training",
                          "/templates",
                          "/sensors",
                          "/reports",
                        ])} ${classes.navLinkWithMenu}`}
                        icon="submenu"
                        subMenu={[
                          {
                            text: langArea.templates,
                            icon: "templates-menu",
                            to: "/templates",
                            requires: readTemplates,
                          },
                          { text: langArea.people, icon: "people", to: "/people", requires: "user_read_admin" },
                          {
                            text: langArea.training,
                            icon: "training",
                            to: "/training",
                            requires: adminTraining,
                            feature: "training",
                          },
                          {
                            text: langArea.sensors,
                            icon: "sensors",
                            to: "/sensors",
                            requires: "sensor_read",
                            feature: "sensors",
                          },
                          {
                            text: langArea.reports,
                            icon: "reports",
                            to: "/reports",
                            requires: readReports,
                            beta: true,
                          },
                        ]}
                        cy="-managers-hub"
                      />
                    )}
                  </nav>
                </Box>

                <HeaderTray onKioskLoggingOut={handleKioskLoggingOut} />
              </>
            )}
          </Toolbar>
        </Container>
        {kiosk && hasTimesheets && <PunchClock onLoaded={handlePunchClockLoaded} />}
      </AppBar>
    </>
  )
}

export default Header
